import { inject, Injectable } from '@angular/core';
import { DbService } from '../db/db.service';
import { Project } from '../../models/project';
import { Course } from '../../models/course';
import { LanguageCourse } from '../../models/languageCourse';
import { isNoEmptyArray, isNullOrUndefined, isValidFunction } from '@pineapplelab/util';
import { Category } from '../../models/category';
import { environment } from '../../../environments/environment';
import { MediaLibraryService } from '../media-library/media-library.service';
import firebase from "firebase/compat/app";

@Injectable({
	providedIn: 'root'
})
export class LanguageCourseService {

	private db = inject(DbService);
	private mediaLibraryService = inject(MediaLibraryService);

	observe(
		courseId: string,
		next: (snapshot: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>) => void,
		error: (error: firebase.firestore.FirestoreError) => void,
	) {
		if (!courseId || !isValidFunction(next) || !isValidFunction(error)) {
			return null;
		}
		return this.db.getProyIdRef(environment.projectId)
			.collection(Course.PATH)
			.doc(courseId)
			.collection(LanguageCourse.PATH)
			.onSnapshot(next as any, error);
	}

	async get(courseId: string, projectId: string, language?: string) {
		const languageCoursePath = this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(Course.PATH)
			.doc(courseId)
			.collection(LanguageCourse.PATH);

		let snapshot;
		if (language != null) {
			snapshot = await languageCoursePath.where('language', '==', language)
				.get();
		} else {
			snapshot = await languageCoursePath.where('is_default', '==', true)
				.get();
		}

		if (!snapshot.docs[0].exists) {
			return null;
		}

		const courseDoc = snapshot.docs[0];
		const course = new LanguageCourse(courseDoc.data());
		return course;
	}

	async save(model: LanguageCourse, projectId: string, courseId: string) {
		const json = model.toJson();
		await this.db.getProyIdRef(projectId).collection(Course.PATH).doc(courseId).collection(LanguageCourse.PATH)
			.doc(model.id).set(json, { merge: true });
		return json;
	}

	async saveLanguageCourses(course: Course, projectId: string, categoryId: string) {
		const courseId = course.id;
		const languageCoursesSnapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(Course.PATH)
			.doc(courseId)
			.collection(LanguageCourse.PATH)
			.get();

		for (const langItem of languageCoursesSnapshot.docs) {
			const languageCourseData = langItem.data();
			const languageCourse = new LanguageCourse(languageCourseData);
			if (isNullOrUndefined(languageCourse.id)) {
				languageCourse.id = this.db.generateFirebaseUniqueId();
			}
			const json = languageCourse.toJson();

			await this.db.firestore
				.collection(Project.PATH)
				.doc(projectId)
				.collection(Course.PATH)
				.doc(courseId)
				.collection(LanguageCourse.PATH)
				.get();

			for (const langItem of languageCoursesSnapshot.docs) {
				const languageCourseData = langItem.data();
				const languageCourse = new LanguageCourse(languageCourseData);
				if (isNullOrUndefined(languageCourse.id)) {
					languageCourse.id = this.db.generateFirebaseUniqueId();
				}
				const json = languageCourse.toJson();

				await this.db.firestore
					.collection(Project.PATH)
					.doc(projectId)
					.collection(Category.PATH)
					.doc(categoryId)
					.collection(Course.PATH)
					.doc(course.id)
					.collection(LanguageCourse.PATH)
					.doc(languageCourse.id)
					.set(json, { merge: true });
			}
		}
	}

	async getById(courseId: string, projectId: string, includeVideos: boolean = false, includeIsDeleted: boolean = true) {
		const snapshot = await this.db.getProyIdRef(projectId).collection(Course.PATH)
			.doc(courseId).collection(LanguageCourse.PATH).orderBy('created_at', 'asc').get();
		if (snapshot.empty) {
			return null;
		}
		let languageCourses = snapshot.docs.filter(languageCourse => languageCourse.exists).map(languageCourse => new LanguageCourse(languageCourse.data()));
		if (includeVideos) {
			languageCourses = await this.getLanguageCoursesWithVideos(languageCourses);
		}
		if (!includeIsDeleted) {
			return languageCourses.filter(course => !course?.isDeleted);
		}
		return languageCourses;
	}

	async getDefault(courseId: string, projectId: string) {
		const snapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(Course.PATH)
			.doc(courseId)
			.collection(LanguageCourse.PATH)
			.where('is_default', '==', true)
			.get();

		if (snapshot.empty) {
			return null;
		}

		const languageCourseDoc = snapshot.docs[0];
		const languageCourse = new LanguageCourse(languageCourseDoc.data());
		return languageCourse;
	}

	private async getLanguageCoursesWithVideos(languageCourses: LanguageCourse[]): Promise<LanguageCourse[]> {
		if (!languageCourses || !isNoEmptyArray(languageCourses)) {
			return [];
		}

		const promises = languageCourses.flatMap(languageCourse =>
			languageCourse.chapters.map(async chapter => {
				if (!isNullOrUndefined(chapter?.videoId)) {
					chapter.__fileVideo = await this.mediaLibraryService.getVideoById(environment.projectId, chapter.videoId);
				}
			})
		);

		await Promise.all(promises);
		return languageCourses;
	}

	async delete(courseId: string, languageCourseId: string): Promise<void> {
		if (!courseId || !languageCourseId) {
			return;
		}

		try {
			await this.db.getProyIdRef(environment.projectId)
				.collection(Course.PATH)
				.doc(courseId)
				.collection(LanguageCourse.PATH)
				.doc(languageCourseId)
				.set({ is_deleted: true, deleted_at: firebase.firestore.Timestamp.now() }, { merge: true });
		} catch (error) {}
	}
}
